import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Plus } from 'lucide-react';
import useDropdownPlacement from '../hooks/useDropdownPlacement';
import { markDialogDirty } from '../hooks/useDialog';

/**
 * Searchable select dropdown — a styled select with a built-in search box. Shared across the app
 * (User Management, Timezone Settings, …).
 *
 * Pass `allowCustom` to let the user commit whatever they typed even if it's not in `options` — they
 * pick from filtered suggestions OR choose the "Use '…'" entry at the bottom to keep their typed text.
 *
 * The search input is decoupled from `value`: it's a transient filter that resets each time the
 * dropdown opens, so picking a value never makes the other options disappear. Options are always
 * sorted alphabetically (project-wide convention).
 *
 * Keyboard handling matches the shared Select. ArrowDown opens it, the arrow keys move a visible
 * highlight through the options and onto the "Use '…'" entry, Enter picks the highlighted entry,
 * Escape closes from wherever focus is, Tab closes and moves on, the options are not tab stops, and
 * closing from the keyboard returns focus to the field.
 *
 * Typing moves the highlight to the option that matches exactly, or else to the first option that
 * contains the text, so Enter picks a real option rather than the half-typed text. Enter used to
 * keep whatever had been typed: "Asia/K" was saved as a timezone, and an option whose name sits
 * inside another ("Pharmacist" inside "Senior Pharmacist") could not be chosen from the keyboard.
 *
 * Props: { label, value, onChange, options, placeholder, error, icon, allowCustom }
 */
const SEARCH_H = 56;  // the search row above the list
const LIST_MAX = 192; // matches the old max-h-48
const LIST_MIN = 130; // below this, flipping beats shrinking
const ROW_H = 41;

const SearchableSelect = ({ label, value, onChange, options, placeholder, error, icon, allowCustom = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const anchorRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const sortedOptions = [...options].sort((a, b) => a.localeCompare(b));
  const filtered = query
    ? sortedOptions.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : sortedOptions;
  const customHint =
    allowCustom &&
    query.trim() &&
    !options.some(o => o.toLowerCase() === query.trim().toLowerCase())
      ? query.trim()
      : null;
  // What the arrow keys move through: the options, then the "Use '…'" entry when there is one.
  const entryCount = filtered.length + (customHint ? 1 : 0);

  // Closing from the keyboard returns focus to the field, so the next Tab carries on from it rather
  // than from the top of the page.
  const close = () => {
    setQuery('');
    setOpen(false);
    buttonRef.current?.focus();
  };

  const openList = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!open) return;
    setQuery('');
    // Start from the current value, so the arrow keys move on from what is already chosen.
    setHighlight(sortedOptions.indexOf(value));
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    // On the document, in the capture phase, so Escape closes the list wherever focus is (a press
    // on an option that never becomes a click leaves focus on that option, where a handler on the
    // search box never hears it), and closes only the list, not a modal behind it.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('keydown', onKey, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Typing highlights the exact match if there is one, else the first option containing the text,
  // else the "Use '…'" entry, which is then the only entry.
  useEffect(() => {
    if (!open || !query) return;
    const q = query.trim().toLowerCase();
    const exact = filtered.findIndex(o => o.toLowerCase() === q);
    setHighlight(exact >= 0 ? exact : (entryCount > 0 ? 0 : -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  // Keep the highlighted entry in view while arrowing through a list taller than its panel.
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const select = (val, fromKeyboard = false) => {
    // Typing in the filter box is not a change to the form (it carries data-no-dirty); picking is.
    if (val !== value) markDialogDirty(wrapperRef.current);
    onChange(val);
    if (fromKeyboard) {
      close();
      return;
    }
    setQuery('');
    setOpen(false);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, entryCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && highlight < filtered.length) select(filtered[highlight], true);
      else if (customHint && highlight === filtered.length) select(customHint, true);
      else close();
    } else if (e.key === 'Tab') {
      // Not prevented: focus returns to the field and the browser's Tab moves on from there, past
      // the options, which are not tab stops.
      close();
    }
  };

  // Anchored to the button rather than the whole field, so a flipped panel sits against the
  // control instead of leaving the label stranded between the two.
  const { dropUp, space } = useDropdownPlacement(open, anchorRef, {
    wantHeight: SEARCH_H + Math.min(filtered.length * ROW_H, LIST_MAX),
    minHeight: SEARCH_H + LIST_MIN,
  });

  return (
    <div ref={wrapperRef} className="relative">
      {label && <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>}
      <div className="relative" ref={anchorRef}>
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 0); }}
          onKeyDown={(e) => {
            if (!open && e.key === 'ArrowDown') {
              e.preventDefault();
              openList();
            }
          }}
          className={`w-full flex items-center gap-2 p-3.5 border-2 rounded-xl text-left transition-all ${
            error ? 'border-red-300 bg-red-50' : open ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span className={value ? 'text-gray-900 font-medium' : 'text-gray-400'}>{value || placeholder || 'Select...'}</span>
          <ChevronDown className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div
            className={`absolute left-0 right-0 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden
              ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          >
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  data-no-dirty
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                  placeholder={allowCustom ? 'Type to filter or add a new one…' : 'Search...'}
                />
              </div>
            </div>
            <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: Math.max(LIST_MIN, Math.min(LIST_MAX, space - SEARCH_H)) }}>
              {filtered.length === 0 && !customHint && (
                <div className="px-4 py-3 text-sm text-gray-400">No matches</div>
              )}
              {filtered.map((opt, i) => (
                <button
                  key={opt}
                  type="button"
                  tabIndex={-1}
                  data-idx={i}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(opt)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                    highlight === i
                      ? 'bg-emerald-600 text-white font-medium'
                      : opt === value
                        ? 'bg-emerald-50 text-emerald-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {opt}
                </button>
              ))}
              {customHint && (
                <button
                  type="button"
                  tabIndex={-1}
                  data-idx={filtered.length}
                  onMouseEnter={() => setHighlight(filtered.length)}
                  onClick={() => select(customHint)}
                  className={`w-full text-left px-4 py-2.5 text-sm border-t border-gray-100 flex items-center gap-2 transition-colors ${
                    highlight === filtered.length ? 'bg-emerald-600 text-white' : 'text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  Use <span className="font-semibold">"{customHint}"</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default SearchableSelect;
